/** Geometry always uses natural image pixels, independently of display size and PPCM. */
export interface AreaRectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ImageSize {
    width: number;
    height: number;
}

export interface ImagePoint {
    x: number;
    y: number;
}

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const isValidRectangle = (rect: AreaRectangle, image: ImageSize) =>
    [rect.x, rect.y, rect.width, rect.height, image.width, image.height].every(Number.isFinite) &&
    image.width > 0 &&
    image.height > 0 &&
    rect.width >= 1 &&
    rect.height >= 1 &&
    rect.x >= 0 &&
    rect.y >= 0 &&
    rect.x + rect.width <= image.width + 0.0001 &&
    rect.y + rect.height <= image.height + 0.0001;

export const moveRectangle = (rect: AreaRectangle, dx: number, dy: number, image: ImageSize): AreaRectangle => ({
    ...rect,
    x: clampValue(rect.x + dx, 0, Math.max(0, image.width - rect.width)),
    y: clampValue(rect.y + dy, 0, Math.max(0, image.height - rect.height))
});

/** The opposite edge/corner stays fixed; locked edge handles resize about the other axis' centre. */
export const resizeRectangle = (rect: AreaRectangle, handle: ResizeHandle, dx: number, dy: number, image: ImageSize, ratio: number | null): AreaRectangle => {
    const west = handle.includes('w');
    const east = handle.includes('e');
    const north = handle.includes('n');
    const south = handle.includes('s');
    const anchorX = west ? rect.x + rect.width : east ? rect.x : rect.x + rect.width / 2;
    const anchorY = north ? rect.y + rect.height : south ? rect.y : rect.y + rect.height / 2;
    const maxWidth = west ? anchorX : east ? image.width - anchorX : 2 * Math.min(anchorX, image.width - anchorX);
    const maxHeight = north ? anchorY : south ? image.height - anchorY : 2 * Math.min(anchorY, image.height - anchorY);
    let width = rect.width + (west ? -dx : east ? dx : 0);
    let height = rect.height + (north ? -dy : south ? dy : 0);

    if (ratio && Number.isFinite(ratio) && ratio > 0) {
        if ((!west && !east) || ((north || south) && Math.abs(dy) * ratio > Math.abs(dx))) width = height * ratio;
        const limit = Math.min(maxWidth, maxHeight * ratio);
        const minimum = Math.max(1, ratio);
        if (limit < minimum) return rect;
        width = clampValue(width, minimum, limit);
        height = width / ratio;
    } else {
        width = clampValue(width, 1, maxWidth);
        height = clampValue(height, 1, maxHeight);
    }

    return {
        x: west ? anchorX - width : east ? anchorX : anchorX - width / 2,
        y: north ? anchorY - height : south ? anchorY : anchorY - height / 2,
        width,
        height
    };
};

/** Draw in either direction; a locked ratio is measured in image pixels, not CSS percentages. */
export const drawRectangle = (from: ImagePoint, to: ImagePoint, image: ImageSize, ratio: number | null): AreaRectangle => {
    let width = Math.abs(to.x - from.x);
    let height = Math.abs(to.y - from.y);
    const right = to.x >= from.x;
    const down = to.y >= from.y;
    const maxWidth = right ? image.width - from.x : from.x;
    const maxHeight = down ? image.height - from.y : from.y;
    if (ratio && Number.isFinite(ratio) && ratio > 0) {
        width = Math.min(Math.max(width, height * ratio), maxWidth, maxHeight * ratio);
        height = width / ratio;
    } else {
        width = Math.min(width, maxWidth);
        height = Math.min(height, maxHeight);
    }
    return { x: right ? from.x : from.x - width, y: down ? from.y : from.y - height, width, height };
};

/** Numeric dimensions keep the centre wherever possible and move just enough to stay on the image. */
export const setRectangleSize = (rect: AreaRectangle, width: number, height: number, image: ImageSize): AreaRectangle | null => {
    if (![width, height].every(Number.isFinite) || width < 1 || height < 1 || width > image.width || height > image.height) return null;
    return {
        x: clampValue(rect.x + (rect.width - width) / 2, 0, image.width - width),
        y: clampValue(rect.y + (rect.height - height) / 2, 0, image.height - height),
        width,
        height
    };
};
